import React, { useEffect, useRef } from 'react';
import "./Shootingstar.css";

const ShootingStar = () => {
  const canvasRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const mouseMovedRef = useRef(false);
  const lastMouseMoveRef = useRef(Date.now());

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    let pointer = {
      x: 0.5 * window.innerWidth,
      y: 0.5 * window.innerHeight,
    };

    const params = {
      pointsNumber: 40,
      widthFactor: 0.8,
      spring: 0.4,
      friction: 0.5,
    };

    const trail = new Array(params.pointsNumber);
    for (let i = 0; i < params.pointsNumber; i++) {
      trail[i] = {
        x: pointer.x,
        y: pointer.y,
        dx: 0,
        dy: 0,
      };
    }

    const startColor = getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim();
    const endColor1 = '#f1e7da';

    const interpolateColor = (color1, color2, factor) => {
      const [r1, g1, b1] = [parseInt(color1.slice(1, 3), 16), parseInt(color1.slice(3, 5), 16), parseInt(color1.slice(5, 7), 16)];
      const [r2, g2, b2] = [parseInt(color2.slice(1, 3), 16), parseInt(color2.slice(3, 5), 16), parseInt(color2.slice(5, 7), 16)];
      const r = Math.round(r1 + factor * (r2 - r1));
      const g = Math.round(g1 + factor * (g2 - g1));
      const b = Math.round(b1 + factor * (b2 - b1));
      return `rgb(${r}, ${g}, ${b})`;
    };

    const updateMousePosition = (eX, eY) => {
      pointer.x = eX;
      pointer.y = eY;
      lastMouseMoveRef.current = Date.now(); // Update last mouse move time
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(() => {
        mouseMovedRef.current = false;
      }, 2000); // 2 seconds of inactivity
    };

    const handleClick = (e) => {
      updateMousePosition(e.pageX, e.pageY);
    };

    const handleMouseMove = (e) => {
      mouseMovedRef.current = true;
      updateMousePosition(e.pageX, e.pageY);
    };

    const handleTouchMove = (e) => {
      mouseMovedRef.current = true;
      updateMousePosition(e.targetTouches[0].pageX, e.targetTouches[0].pageY);
    };

    const handleMouseOut = () => {
      mouseMovedRef.current = false;
    };

    const setupCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const update = (t) => {
      const radius = 0.43 * Math.min(window.innerWidth, window.innerHeight);
      const centerX = window.innerWidth / 1;
      const centerY = window.innerHeight / 2;
      const angle = 0.001 * t * 2.2;

      if (mouseMovedRef.current) {
        // Follow the mouse
        // No need to update pointer here; it's updated in updateMousePosition
      } else {
        // Circular motion
        pointer.x = centerX + radius * Math.cos(angle);
        pointer.y = centerY + radius * Math.sin(angle);
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const cycleDuration = 10000;
      const progress = (Math.sin(t / cycleDuration * 2 * Math.PI) + 1) / 2;
      const color = interpolateColor(startColor, endColor1, progress);
      ctx.strokeStyle = color;

      const width = 1;
      ctx.lineWidth = Math.max(1, width);

      trail.forEach((p, pIdx) => {
        const prev = pIdx === 0 ? pointer : trail[pIdx - 1];
        const spring = pIdx === 0 ? 0.4 * params.spring : params.spring;
        p.dx += (prev.x - p.x) * spring;
        p.dy += (prev.y - p.y) * spring;
        p.dx *= params.friction;
        p.dy *= params.friction;
        p.x += p.dx;
        p.y += p.dy;
      });

      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(trail[0].x, trail[0].y);

      for (let i = 1; i < trail.length - 1; i++) {
        const xc = 0.5 * (trail[i].x + trail[i + 1].x);
        const yc = 0.5 * (trail[i].y + trail[i + 1].y);
        ctx.quadraticCurveTo(trail[i].x, trail[i].y, xc, yc);
        ctx.stroke();
      }
      ctx.lineTo(trail[trail.length - 1].x, trail[trail.length - 1].y);
      ctx.stroke();

      window.requestAnimationFrame(update);
    };

    setupCanvas();
    update(0);

    window.addEventListener("click", handleClick);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("mouseout", handleMouseOut);
    window.addEventListener("resize", setupCanvas);

    return () => {
      window.removeEventListener("click", handleClick);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("mouseout", handleMouseOut);
      window.removeEventListener("resize", setupCanvas);
      clearTimeout(inactivityTimerRef.current);
    };
  }, []);

  return <canvas ref={canvasRef} className="canvas" />;
};

export default ShootingStar;
